import React, { useState } from "react";
import { Dropdown, Button, Icon } from "semantic-ui-react";
import ReactTable, { ReactTableDefaults } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/pro-regular-svg-icons";
import { saveAs } from "file-saver";
import moment from "moment";

import { QUESTION_RESULT_STATUS } from "../../Appointments/common";
import { getYYYMMDD } from "../../../util/common";
import Service from "../service";

const QualityChecks = ({ report, t, filters, page, onFilterChange, onPageChange, isReloading }) => {
  const [isExporting, setIsExporting] = useState(false);

  const payload = report.report_data?.[0] ? JSON.parse(report.report_data[0]) : {};
  const rows = payload.rows || [];
  const pages = payload.pages || 0;
  const payloadFilters = payload.filters || {};
  const mechanics = payloadFilters.mechanics || [];
  const qc_officers = payloadFilters.qc_officers || [];
  const dealer_locations = payloadFilters.dealer_locations || [];
  const quality_checklists = payloadFilters.quality_checklists || [];

  const handleFilterChange = (_, { name, value }) => onFilterChange({ ...filters, [name]: value === "" ? null : value });

  const exportData = () => {
    const labels = {
      location: t("location").message || "Location",
      wo_date: t("wo_date").message || "WO date",
      wo_nr: t("wo_nr").message || "WO Nr",
      check_date: t("check_date").message || "Check date",
      mechanic: t("mechanic").message || "Mechanic",
      qc_status: t("qc_status").message || "QC Status",
      qc_officer: t("qc_officier").message || "QC Officier",
      qc_checklist: t("qc_checklist").message || "QC Checklist",
      ok_with_note_remark: t("ok_with_note_remark").message || "OK With Note Remarks",
      advised_remark: t("advised_remark").message || "Advised Remarks",
      critical_remark: t("critical_remark").message || "Critical Remarks",
    };

    const exportRows = rows.map(d => ({
      [labels.location]: d.location ?? "",
      [labels.wo_date]: d.wo_date ? moment(d.wo_date).format("DD-MM-YYYY") : "",
      [labels.wo_nr]: d.wo_nr ?? "",
      [labels.check_date]: d.check_date ? moment(d.check_date).format("DD-MM-YYYY") : "",
      [labels.mechanic]: d.mechanic ?? "",
      [labels.qc_status]: getQCStatusText(d.qc_status),
      [labels.qc_officer]: d.qc_officer ?? "",
      [labels.qc_checklist]: d.qc_checklist ?? "",
      [labels.ok_with_note_remark]: d.ok_with_note_remark ?? "",
      [labels.advised_remark]: d.advised_remark ?? "",
      [labels.critical_remark]: d.critical_remark ?? "",
    }));

    setIsExporting(true);

    Service.downloadODS(Object.values(labels), exportRows)
      .then(res => saveAs(new Blob([res.data], { type: "application/vnd.oasis.opendocument.spreadsheet" }), "QualityChecks_" + getYYYMMDD() + ".ods"))
      .catch(error => console.error("Error exporting quality checks.", error))
      .finally(() => setIsExporting(false));
  };

  const handleAppointmentClick = appointment_id => {
    window.open(`${document.location.origin}/#!/appointments/${appointment_id}`, "_blank");
  };

  const getQuestionStatusOptions = () => [
    {
      value: QUESTION_RESULT_STATUS.OK,
      text: (
        <span style={{ color: "green" }}>
          <Icon name="check" />
          <span>{t("OK").message || "OK"}</span>
        </span>
      ),
      className: "positive",
      color: "green",
    },
    {
      value: QUESTION_RESULT_STATUS.OK_WITH_REMARKS,
      text: (
        <span style={{ color: "green" }}>
          <Icon name="file outline" />
          <span>{t("ok_with_a_note").message || "OK with a note"}</span>
        </span>
      ),
      className: "positive",
      color: "green",
    },
    {
      value: QUESTION_RESULT_STATUS.ADVISED,
      text: (
        <span style={{ color: "orange" }}>
          <Icon name="attention" />
          <span>{t("advised").message || "Advised"}</span>
        </span>
      ),
      className: "warning",
      color: "orange",
    },
    {
      value: QUESTION_RESULT_STATUS.NECESSARY,
      text: (
        <span style={{ color: "red" }}>
          <Icon name="dont" />
          <span>{t("Critical").message || "Critical"}</span>
        </span>
      ),
      className: "emergency",
      color: "red",
    },
  ];

  const getQCStatusText = status => {
    switch (status) {
      case QUESTION_RESULT_STATUS.OK:
        return t("OK").message || "OK";
      case QUESTION_RESULT_STATUS.OK_WITH_REMARKS:
        return t("ok_with_a_note").message || "OK with a note";
      case QUESTION_RESULT_STATUS.ADVISED:
        return t("advised").message || "Advised";
      case QUESTION_RESULT_STATUS.NECESSARY:
        return t("Critical").message || "Critical";
      default:
        return "";
    }
  };

  return (
    <>
      <h3 className="report-heading -margin-bottom-10">
        {t(report.report_name).message || report.report_name}
        <Button color="green" floated="right" basic loading={isExporting} disabled={isExporting} onClick={exportData}>
          <span style={{ marginRight: "10px" }}>{t("export").message || "Export"}</span>
          <FontAwesomeIcon icon={faFileExport} />
        </Button>
      </h3>
      <div className="dealer-reports-table-container">
        <Dropdown
          name="status"
          options={getQuestionStatusOptions()}
          placeholder={t("select_qc_status").message || "Select QC Status"}
          selection
          clearable
          selectOnBlur={false}
          value={filters.status ?? ""}
          onChange={handleFilterChange}
        />

        <Dropdown
          name="mechanic_id"
          options={mechanics.map(m => ({ value: m.id, text: (t("mechanic").message || "Mechanic") + " " + m.name }))}
          placeholder={t("select_mechanic").message || "Select Mechanic"}
          selection
          search
          clearable
          selectOnBlur={false}
          value={filters.mechanic_id ?? ""}
          onChange={handleFilterChange}
        />

        <Dropdown
          name="qc_officer_id"
          options={qc_officers.map(o => ({ value: o.id, text: (t("qc_officier").message || "QC Officier") + " " + o.name }))}
          placeholder={t("select_qc_officier").message || "Select QC Officier"}
          selection
          search
          clearable
          selectOnBlur={false}
          value={filters.qc_officer_id ?? ""}
          onChange={handleFilterChange}
        />

        <Dropdown
          name="dealer_location_id"
          options={dealer_locations.map(l => ({ value: l.id, text: l.name }))}
          placeholder={t("select_location").message || "Select Location"}
          selection
          search
          clearable
          selectOnBlur={false}
          value={filters.dealer_location_id ?? ""}
          onChange={handleFilterChange}
        />

        <Dropdown
          name="quality_checklist_id"
          options={quality_checklists.map(c => ({ value: c.id, text: c.name }))}
          placeholder={t("select_checklist").message || "Select Checklist"}
          selection
          search
          clearable
          selectOnBlur={false}
          value={filters.quality_checklist_id ?? ""}
          onChange={handleFilterChange}
        />

        <ReactTable
          className="ReactTable -floated-table no-overflow all-data-table -initial-margin hasLongRemark"
          manual
          loading={isReloading}
          data={rows}
          page={page - 1}
          pages={pages}
          onFetchData={state => {
            if (state.page + 1 !== page) onPageChange(state.page + 1);
          }}
          showPagination={pages > 1}
          showPageSizeOptions={false}
          nextText={t("next").message || "Next"}
          previousText={t("previous").message || "Previous"}
          pageText={t("page").message || "Page"}
          ofText={t("of").message || "of"}
          sortable={false}
          minRows={0}
          onPageChange={() => document.querySelector(".ReactTable.hasLongRemark .rt-tbody").scrollTo(0, 0)}
          style={{ maxHeight: "600px" }}
          resizable={false}
          pageSize={rows.length || 0}
          noDataText={
            <div className="Table__no-results">
              <p>{t("no_data").message || "No data"}</p>
            </div>
          }
          column={{
            ...ReactTableDefaults.column,
            headerClassName: "ReactTable__column-header dealer-table-header",
            className: "ReactTable__column",
          }}
          columns={[
            { Header: <strong>{t("location").message || "Location"}</strong>, accessor: "location" },
            {
              Header: <strong>{t("wo_date").message || "WO date"}</strong>,
              accessor: "wo_date",
              Cell: cell => moment(cell.value).format("D MMM, YYYY"),
            },
            {
              Header: <strong>{t("wo_nr").message || "WO Nr"}</strong>,
              id: "wo_nr",
              accessor: d => (
                <span onClick={() => handleAppointmentClick(d.appointment_id)} className="quality_checks_wo_cell">
                  {d.wo_nr}
                </span>
              ),
            },
            {
              Header: <strong>{t("check_date").message || "Check date"}</strong>,
              accessor: "check_date",
              Cell: cell => moment(cell.value).format("D MMM, YYYY"),
            },
            { Header: <strong>{t("mechanic").message || "Mechanic"}</strong>, accessor: "mechanic" },
            {
              Header: <strong>{t("qc_status").message || "QC Status"}</strong>,
              id: "qc_status",
              accessor: d => <span>{getQCStatusText(d.qc_status)}</span>,
            },
            { Header: <strong>{t("qc_officier").message || "QC Officier"}</strong>, accessor: "qc_officer" },
            { Header: <strong>{t("qc_checklist").message || "QC Checklist"}</strong>, accessor: "qc_checklist" },
            {
              Header: <strong>{t("ok_with_note_remark").message || "OK With Note Remarks"}</strong>,
              accessor: "ok_with_note_remark",
              style: { whiteSpace: "initial", textAlign: "left" },
            },
            {
              Header: <strong>{t("advised_remark").message || "Advised Remarks"}</strong>,
              accessor: "advised_remark",
              style: { whiteSpace: "initial", textAlign: "left" },
            },
            {
              Header: <strong>{t("critical_remark").message || "Critical Remarks"}</strong>,
              accessor: "critical_remark",
              style: { whiteSpace: "initial", textAlign: "left" },
            },
          ]}
        />
      </div>
    </>
  );
};

export { QualityChecks };
